import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import work4hrIcon from "@/assets/brand/work4hr-icon.png";
import {
  LayoutDashboard, Users, Building2, CalendarDays, Wallet, Briefcase,
  FileText, Laptop, TrendingUp, LogOut, PanelLeftClose, PanelLeft, BookUser, Network, CalendarClock, FileCheck,
  Layers, MapPin, ScrollText, Clock, Globe, Mail, ChevronDown, Sparkles, SlidersHorizontal, CloudDownload, History, Monitor, Megaphone,
  Receipt, ClipboardCheck, Tag, PiggyBank, UserCheck,
  BookOpen, Activity, BadgeCheck, GraduationCap, FileSignature, UserPen, Clock3, BriefcaseBusiness, HandCoins,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/app/store/auth.store";
import api from "@/lib/api/client";
import { useUIStore } from "@/app/store/ui.store";
import { canAny } from "@/lib/utils/permissions";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/ui/button";
import "@/styles/sidebar-curves.css";

// `show(ctx, has)` narrows an item beyond its permissions, using facts about
// the user from GET /dashboard/nav-context (see navContext.service.js): a
// manager with no reports doesn't get team pages, someone never on an
// interview panel doesn't get My interviews, and a login with no employee
// record doesn't get personal pages. It only shapes the menu -- routes and
// the API still enforce permissions on their own.
const hasTeamOr = (...perms) => (ctx, has) => has(...perms) || ctx.teamSize > 0;
const employeeOr = (...perms) => (ctx, has) => has(...perms) || ctx.hasEmployeeRecord;

// Nav is data, not markup. Each item declares what it needs, and the sidebar
// filters -- so a new module cannot accidentally appear for the wrong role.
// A function, not a plain array, only because of the one item below that
// needs to know the signed-in user's own employeeId to build its own link
// (My documents) -- every other item is a fixed route.
function buildNav(ownEmployeeId) {
  return [
  { section: null, items: [{ to: "/", label: "Overview", icon: LayoutDashboard, end: true }] },
  {
    section: "People", sectionIcon: Users,
    items: [
      { to: "/onboarding", label: "Onboarding", icon: UserCheck, require: ["onboarding:manage"] },
      { to: "/employees", label: "Employees", icon: Users, require: ["employee:read:all", "employee:read:team"], show: hasTeamOr("employee:read:all") },
      // Separate modules, deliberately not mixed into Employees.
      { to: "/part-time", label: "Part-time employees", icon: Clock3, require: ["employee:read:all", "employee:read:team"], show: hasTeamOr("employee:read:all") },
      { to: "/freelancers", label: "Freelancers", icon: BriefcaseBusiness, require: ["freelancer:manage"] },
      { to: "/directory", label: "Directory", icon: BookUser },
      { to: "/org-chart", label: "Org chart", icon: Network },
    ],
  },
  {
    section: "Lifecycle", sectionIcon: Activity,
    items: [
      { to: "/lifecycle", label: "Lifecycle overview", icon: Activity, require: ["employee:read:all"], end: true },
      { to: "/lifecycle/probation", label: "Probation", icon: BadgeCheck, require: ["employee:write:all"] },
      { to: "/lifecycle/interns", label: "Interns", icon: GraduationCap, require: ["employee:read:all"] },
      { to: "/resignations", label: "Resignations", icon: LogOut, require: ["exit:manage", "employee:read:team"], show: hasTeamOr("exit:manage") },
      { to: "/letters", label: "HR letters", icon: FileSignature, require: ["letter:manage"] },
      { to: "/profile-changes", label: "Profile changes", icon: UserPen, require: ["employee:read:all"] },
      // Only for whoever actually approves or processes freelancer payments.
      { to: "/freelancer-payments", label: "Freelancer payments", icon: HandCoins, show: (ctx, has) => has("freelancer:manage") || ctx.isFreelancerApprover },
      // Everyone's own resignation / notice period / exit letters.
      { to: "/resignation", label: "My resignation", icon: LogOut, require: ["resignation:submit"], personal: true, show: (ctx) => ctx.hasEmployeeRecord && !["PRE_JOINING", "EXITED"].includes(ctx.employeeStatus) },
      { to: "/settings/letter-templates", label: "Letter templates", icon: FileCheck, require: ["letter:manage"] },
    ],
  },
  {
    section: "Recruitment", sectionIcon: Briefcase,
    items: [
      // recruitment:manage:own (managers) only matters once they are the
      // hiring manager on at least one job posting.
      { to: "/recruitment", label: "Pipeline", icon: Briefcase, require: ["recruitment:manage", "recruitment:manage:own"], show: (ctx, has) => has("recruitment:manage") || ctx.isHiringManager },
      { to: "/recruitment/candidates", label: "Candidates", icon: Users, require: ["recruitment:manage", "recruitment:manage:own"], show: (ctx, has) => has("recruitment:manage") || ctx.isHiringManager },
      { to: "/offers", label: "Offers", icon: FileCheck, require: ["offer:manage"] },
      // Both entry points (From Recruitment and Direct) live on this one
      // list -- see OnboardingListPage.jsx's own source filter/badges.
      { to: "/settings/offer-letter-template", label: "Offer letter template", icon: Sparkles, require: ["offer:manage"] },
      // No `require` -- deliberately visible to every signed-in employee, the
      // same way Overview is. Being on an interview panel has nothing to do
      // with holding recruitment:manage, and the route behind this is gated
      // the same way: by authentication and panel membership, not by role.
      { to: "/recruitment/my-panel", label: "My interviews", icon: CalendarClock, show: (ctx) => ctx.isPanelist },
      { to: "/settings/job-boards", label: "Job board APIs", icon: Globe, require: ["recruitment:manage"] },
      { to: "/settings/recruitment", label: "Recruitment settings", icon: SlidersHorizontal, require: ["recruitment:manage"] },
      // Grouped here rather than under Organization -- who signs an offer
      // letter is a recruitment/hiring concern, distinct from the
      // Letterhead below (a company-wide fact, set once under
      // Organization). Still gated by org:manage underneath, same as
      // Letterhead -- see OfferLetterSignaturesPage.jsx's own comment.
      { to: "/settings/offer-letter-signatures", label: "Offer letter signatures", icon: FileCheck, require: ["org:manage"] },
    ],
  },
  {
    section: "Leave & attendance", sectionIcon: CalendarDays,
    items: [
      { to: "/leave", label: "Leave / WFH", icon: CalendarDays, require: ["leave:read:own"], show: employeeOr("leave:read:all", "leave:approve:all") },
      { to: "/attendance", label: "Attendance", icon: CalendarDays, require: ["attendance:read:own"], show: employeeOr("attendance:read:all") },
      { to: "/team-activity", label: "Team Activity", icon: Monitor, require: ["attendance:read:team", "attendance:read:all"], show: hasTeamOr("attendance:read:all") },
      // No `require` -- every signed-in employee should be able to see the
      // holiday calendar HR sets, not just admins (the admin-only "Holidays"
      // entry under Organization below stays gated on org:manage since it's
      // the add/edit/delete view, not this read-only one).
      { to: "/holidays", label: "Holidays", icon: CalendarDays },
    ],
  },
  {
    section: "Reimbursements", sectionIcon: Receipt,
    items: [
      // No `require` -- every signed-in employee can submit and track
      // their own claims (expense:submit/expense:read:own are granted to
      // all three non-super-admin roles), same self-branching treatment
      // as Leave/Attendance above.
      { to: "/expenses", label: "My Claims", icon: Receipt, personal: true, show: (ctx) => ctx.hasEmployeeRecord },
      { to: "/expenses-approvals", label: "Pending Approvals", icon: ClipboardCheck, require: ["expense:approve:team", "expense:approve:all"], show: hasTeamOr("expense:approve:all") },
      // Full org-wide claims list (any status) -- Finance/Ops/Super Admin
      // need to see everything, not just what's pending their own action.
      { to: "/expenses-all", label: "All Claims", icon: Layers, require: ["expense:approve:all", "expense:manage"] },
      { to: "/expenses-categories", label: "Categories", icon: Tag, require: ["expense:manage"] },
    ],
  },
  {
    section: "Fund Management", sectionIcon: PiggyBank,
    items: [
      { to: "/funds", label: "Funds", icon: PiggyBank, require: ["fund:manage"] },
    ],
  },
  {
    section: "Operations", sectionIcon: Layers,
    items: [
      // No `require` -- every signed-in user reads and dismisses these
      // (see AnnouncementsPage.jsx), the same "everyone visits, the page
      // itself hides what you can't do" treatment as My interviews above.
      // Only the compose button inside the page is gated on
      // engagement:manage.
      { to: "/announcements", label: "Announcements", icon: Megaphone },
      // Everyone reads/acknowledges policies; HR also manages company
      // statutory records here.
      { to: "/company-records", label: "Company policies", icon: BookOpen, show: (ctx, has) => ctx.hasEmployeeRecord || has("org:manage") },
      { to: "/documents", label: "Documents", icon: FileText, require: ["document:verify"] },
      // Everyone's own shortcut straight to Upload -- before this, getting
      // there meant Directory -> find your own card -> Documents tab ->
      // Upload, every single time. Hidden entirely (rather than shown but
      // broken) when there's no employeeId to link to -- an HR/admin login
      // with no linked employee record has nothing of its own to upload
      // here.
      ...(ownEmployeeId
        ? [{ to: `/employees/${ownEmployeeId}?tab=documents`, label: "My documents", icon: FileCheck, require: ["document:upload:own"] }]
        : []),
      { to: "/assets", label: "Assets", icon: Laptop, require: ["asset:manage", "asset:read:own"], show: employeeOr("asset:manage") },
      { to: "/payroll", label: "Payroll", icon: Wallet, require: ["payroll:read:own"], show: employeeOr("payroll:read:all", "payroll:process") },
      { to: "/performance", label: "Performance", icon: TrendingUp, require: ["performance:read:own"], show: employeeOr("performance:read:all") },
    ],
  },
  {
    section: "Organization", sectionIcon: Building2,
    items: [
      { to: "/settings/organisation/leave-management", label: "Leave Management", icon: ScrollText, require: ["org:manage"] },
      { to: "/settings/organisation/departments", label: "Departments", icon: Building2, require: ["org:manage"] },
      { to: "/settings/organisation/roles", label: "Roles", icon: Layers, require: ["org:manage"] },
      { to: "/settings/organisation/locations", label: "Locations", icon: MapPin, require: ["org:manage"] },
      { to: "/settings/organisation/shifts", label: "Shifts", icon: Clock, require: ["org:manage"] },
      { to: "/settings/organisation/holidays", label: "Holidays", icon: CalendarDays, require: ["org:manage"] },
      { to: "/settings/organisation/offer-letter", label: "Letterhead", icon: FileCheck, require: ["org:manage"] },
      { to: "/settings/organisation/documents", label: "Document Sync", icon: CloudDownload, require: ["org:manage"] },
      { to: "/settings/email", label: "Email settings", icon: Mail, require: ["org:manage"] },
      { to: "/settings/productivity-rules", label: "Productivity Rules", icon: Monitor, require: ["org:manage"] },
      { to: "/settings/activity-logs", label: "Activity Logs", icon: History, require: ["audit:read"] },
    ],
  },
  ];
}

// `active` comes from the Sidebar (the single most specific matching item).
// A plain Link, not NavLink: NavLink sets aria-current="page" on its own
// for "/recruitment" while on "/recruitment/candidates", and the sidebar
// CSS styles the active row off aria-current -- so Pipeline lit up
// alongside Candidates (and My interviews).
function NavItem({ item, collapsed, nested, active }) {
  const Icon = item.icon;
  const isActive = active;
  return (
    <Link
      to={item.to}
      aria-current={isActive ? "page" : undefined}
      className={
        cn(
          "workspace-nav-item group relative flex min-h-11 items-center gap-3 text-[16px] font-bold tracking-[-0.01em] transition-all duration-200",
          collapsed
            ? "mx-2 justify-center rounded-xl px-2 py-2"
            // Rounded only on the left, square on the right -- the active
            // (and hover) row runs flush to the sidebar's own right edge
            // instead of floating as an isolated pill, so it reads as a tab
            // opening into the content area rather than a badge in a list.
            : "workspace-nav-item-expanded rounded-l-2xl rounded-r-none py-2 pl-4 pr-5",
          isActive ? "bg-white text-[#5c3fce]" : "text-white/80 hover:bg-white/10 hover:text-white"
        )}
      title={collapsed ? item.label : undefined}
    >
      {(
        <>
          {/* nested (inside a named section, not the standalone Overview
              row) gets a visibly smaller icon badge than its own section
              header uses (h-6 here vs h-7 there) -- before this, a child
              item's own icon circle was pixel-identical to its parent
              section's, with no size, shape, or indentation difference at
              all, which is exactly why the two were impossible to tell
              apart while scrolling a long sidebar. */}
          <span
            className={cn(
              "workspace-nav-icon grid shrink-0 place-items-center",
              nested ? "h-6 w-6" : "h-7 w-7"
            )}
          >
            <Icon className={nested ? "h-3.5 w-3.5 fill-none stroke-[2.15]" : "h-[15px] w-[15px] fill-none stroke-[2.15]"} aria-hidden="true" />
          </span>
          {!collapsed && (
            <>
              <span className="truncate">{item.label}</span>
              {item.soon && (
                <span
                  className={cn(
                    "ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[15px] font-bold",
                    isActive ? "bg-[#5c3fce]/10 text-[#5c3fce]" : "bg-white/15 text-white/80"
                  )}
                >
                  Soon
                </span>
              )}
            </>
          )}
        </>
      )}
    </Link>
  );
}

// The nav sections this user can actually see -- shared by the Sidebar
// and the Topbar's ModuleSearch, so search never offers a page the menu
// itself would hide.
export function useNavSections() {
  const permissions = useAuthStore((s) => s.permissions);
  // Shared admin@ / hr@ logins (User.isSystemAccount) only manage and
  // approve -- no "My documents" (no employee id passed) and no other
  // `personal: true` item for them.
  const isSystemAccount = useAuthStore((s) => Boolean(s.user?.isSystemAccount));
  const ownEmployeeId = useAuthStore((s) => (s.user?.isSystemAccount ? null : s.user?.employeeId));

  // Until the real context arrives, assume the common case (has an
  // employee record, no team, no panel/approver roles) so ordinary
  // employee items render immediately and the rest appear once known.
  const { data: navContext } = useQuery({
    queryKey: ["nav-context", ownEmployeeId],
    queryFn: () => api.get("/dashboard/nav-context").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const ctx = navContext || { hasEmployeeRecord: Boolean(ownEmployeeId), employeeStatus: null, teamSize: 0, isPanelist: false, isHiringManager: false, isFreelancerApprover: false };
  const has = (...perms) => canAny(permissions, ...perms);

  return buildNav(ownEmployeeId).map((s) => ({
    ...s,
    items: s.items.filter((i) =>
      (!i.require || canAny(permissions, ...i.require)) &&
      !(isSystemAccount && i.personal) &&
      (!i.show || i.show(ctx, has))),
  })).filter((s) => s.items.length);
}

export function Sidebar({ onNavigate, forceExpanded = false }) {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const collapsed = forceExpanded ? false : sidebarCollapsed;
  const location = useLocation();
  const sections = useNavSections();

  // Exactly one highlighted item: of every item whose path matches the
  // current URL (exactly, or as a parent path unless `end`), the longest
  // one wins -- so /recruitment/candidates lights up Candidates only, not
  // Pipeline (/recruitment) as well.
  const pathOf = (to) => to.split("?")[0];
  const matchesRoute = (i) => {
    const p = pathOf(i.to);
    return location.pathname === p || (!i.end && p !== "/" && location.pathname.startsWith(`${p}/`));
  };
  const activeTo = sections
    .flatMap((section) => section.items)
    .filter(matchesRoute)
    .sort((a, b) => pathOf(b.to).length - pathOf(a.to).length)[0]?.to ?? null;

  // Whichever group contains the current route starts expanded -- landing
  // on, say, /offers should show the Recruitment group already open, not
  // require a second click just to see where you are. Every other group
  // starts collapsed, which is the entire point of grouping in the first
  // place: a long flat list of ~20 items becomes a handful of named
  // dropdowns instead. Local state, not persisted -- the sidebar stays
  // mounted across navigation within a session (this only initializes
  // once), and a fresh page load re-deriving "open" from the current
  // route is the more predictable behaviour than remembering whatever was
  // manually toggled last time.
  const [openSections, setOpenSections] = useState(() => {
    const initial = new Set(sections.filter((section) => section.section).map((section) => section.section));
    for (const section of sections) {
      if (section.section && section.items.some((i) => location.pathname === i.to || (!i.end && location.pathname.startsWith(`${i.to}/`)))) {
        initial.add(section.section);
      }
    }
    return initial;
  });

  const toggleSection = (name) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <aside
      className={cn(
        "workspace-sidebar relative flex h-full flex-col overflow-hidden bg-[#5752ae] text-white shadow-[6px_0_24px_rgba(50,35,140,0.22)] transition-[width] duration-300 ease-out dark:shadow-[6px_0_24px_rgba(0,0,0,0.35)]",
        collapsed ? "w-[72px]" : "w-[250px]"
      )}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_20%_0%,rgba(255,255,255,0.14),transparent_62%)]" />
      <div className={cn("relative flex h-16 shrink-0 items-center px-5", collapsed && "justify-center px-3")}>
        {collapsed ? (
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/95 shadow-sm">
            <img src={work4hrIcon} alt="Work4HR" className="h-5 w-5 object-contain" />
          </span>
        ) : (
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/95 shadow-sm">
              <img src={work4hrIcon} alt="Work4HR" className="h-5 w-5 object-contain" />
            </span>
            <p className="truncate text-[22px] font-extrabold uppercase leading-tight tracking-[0.02em] text-white">Work4HR</p>
          </div>
        )}
      </div>

      <nav
        className={cn("sidebar-scroll flex-1 space-y-2 overflow-y-auto pb-4 pt-5", collapsed ? "px-3" : "pl-3 pr-0")}
        onClick={onNavigate}
      >
        {sections.map((section, i) => {
          // The one section with no name (just Overview) is never a
          // dropdown -- there is nothing to group a single top-level item
          // under.
          if (!section.section) {
            return (
              <div key={i} className="space-y-1 pb-2">
                {section.items.map((item) => (
                  <NavItem key={item.to} item={item} collapsed={collapsed} active={item.to === activeTo} />
                ))}
              </div>
            );
          }

          const isOpen = collapsed || openSections.has(section.section);
          const SectionIcon = section.sectionIcon;

          return (
            <div key={section.section} className="space-y-1">
              {collapsed ? (
                <p className="sr-only">{section.section}</p>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation(); // don't let onNavigate (which closes a mobile drawer) fire on a toggle click
                    toggleSection(section.section);
                  }}
                  className="workspace-nav-section flex w-full items-center justify-between rounded-lg px-2.5 pb-2.5 pt-4 text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-white/55 transition-colors hover:text-white/85"
                  aria-expanded={isOpen}
                >
                  <span className="flex items-center gap-2.5">
                    <span className="hidden">
                      <SectionIcon className="h-[15px] w-[15px] fill-none stroke-[2.15]" />
                    </span>
                    {section.section}
                  </span>
                  <ChevronDown className={cn("h-3.5 w-3.5 stroke-[2.6] text-white/50 transition-transform duration-200", isOpen && "rotate-180 text-white/85")} />
                </button>
              )}
              {isOpen && (
                <div className={cn("animate-slide-down space-y-0.5 pb-1", !collapsed && "relative")}>
                  {section.items.map((item) => (
                    <NavItem key={item.to} item={item} collapsed={collapsed} nested active={item.to === activeTo} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="shrink-0 px-3 pb-3">
        <div className="mb-2 h-px bg-white/10" />
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "sm"}
          onClick={toggleSidebar}
          className={cn("mt-1 h-9 w-full rounded-lg font-bold text-white/70 hover:bg-white/10 hover:text-white", !collapsed && "justify-start", forceExpanded && "hidden")}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          {!collapsed && <span className="text-[16px]">Collapse</span>}
        </Button>
      </div>
    </aside>
  );
}
